import React, { useCallback, useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import type {
	ButtonItemConfig,
	ExpandableItemConfig,
	LinkItemConfig,
	ObjectListFlyoutProps,
} from '@atlassian/navigation-common/types';
import {
	type ItemDragAndDropGroup,
	useL1ItemDragAndDrop,
} from '@atlassian/navigation-drag-and-drop';

import { useOptions } from '../../../controllers/options-provider';
import { ObjectListFlyout } from '../../../ui/object-list-flyout';
import { withSideNavigationAnalyticsListener } from '../../analytics/context';
import type { NavItemConfigInternal } from '../../types';
import {
	ElementEventListener,
	ElementEventListenerProvider,
} from '../../utils/element-event-listener';
import { omitSafely } from '../../utils/omit-safely';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../utils/type-guards';
import { useNavAnalytics } from '../../utils/use-nav-analytics';

import { useExpandOpenState } from './use-expand-open-state';

export interface NavItemProps {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	item: NavItemConfigInternal;
	onClick?: () => void;
	// only used for custom items
	onPeek?: () => void;
	peek?: boolean;
}

export const NavItem: React.MemoExoticComponent<typeof NavItemRaw> = React.memo(NavItemRaw);
export default NavItem;

function NavItemRaw({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
	onPeek,
	group = L1_MENU_GROUPS.MENU,
}: NavItemProps): React.ReactNode {
	const { l1DragAndDropEnabled } = useOptions();
	const { type } = item;

	const ItemWrapper = item.ItemWrapper ?? React.Fragment;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);
	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useL1ItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		peek,
		group,
		l1DragAndDropEnabled,
	});

	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();

	// Common internal onClick handler for all top-level item types
	const onNavItemClick = useCallback(
		(isRightClick?: boolean, status?: 'expanded' | 'collapsed') => {
			fg('navx-global-side-nav-confluence')
				? fireClickedSideNavItemEvent(id, undefined, isRightClick, status)
				: fireClickedSideNavItemEvent(id, undefined, isRightClick);
		},
		[fireClickedSideNavItemEvent, id],
	);

	// Common onSubMenuItemClick handler for all sub-menu item types
	const onSubMenuItemClick = useCallback(
		(isRightClick?: boolean, extendedAttributes?: Record<string, unknown>) => {
			fireClickedSideNavSubMenuItemEvent(
				id,
				undefined,
				isInMoreMenu,
				isRightClick,
				undefined,
				extendedAttributes ?? null,
			);
		},
		[fireClickedSideNavSubMenuItemEvent, id, isInMoreMenu],
	);

	useEffect(() => {
		if (isExpandable && isExpandOrFlyoutOpen) {
			fireViewedSideNavSubMenuEvent(id, undefined, isInMoreMenu);
		}
	}, [fireViewedSideNavSubMenuEvent, id, isExpandOrFlyoutOpen, isExpandable, isInMoreMenu, type]);

	if (isCustomItemConfig(item)) {
		const Wrapper = item.isMenuListItem ? React.Fragment : MenuListItem;
		// If the item is a custom item, render it directly
		// TODO: Add customizationHandlers prop when ready.
		return (
			<ItemWrapper>
				<Wrapper>
					<item.Component
						draggableRef={draggableRef}
						dropIndicator={dropIndicator}
						hasDragIndicator={isItemDraggable}
						isDragging={isDragging}
						isExpandOrFlyoutOpen={isExpandOrFlyoutOpen}
						isInMoreMenu={isInMoreMenu && !peek}
						isPeeked={peek}
						onClick={(...args) => {
							item.Component.defaultProps?.onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						onExpandFlyoutToggle={onExpandFlyoutToggle}
						onPeek={onPeek}
						visualContentRef={dropTargetRef}
					/>

					{maybeDragPreview}
				</Wrapper>
			</ItemWrapper>
		);
	} else if (isLinkItemConfig(item)) {
		const { label, labelElem, ...linkMenuItemProps } = omitSafely(item, 'type');

		return (
			<ItemWrapper>
				<ElementEventListener handler={onNavItemClick}>
					<LinkMenuItem
						{...linkMenuItemProps}
						onClick={(...args) => {
							linkMenuItemProps.onClick?.(...args);
							onNavItemClick();
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
						actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
					>
						{labelElem ?? label}
					</LinkMenuItem>
					{maybeDragPreview}
				</ElementEventListener>
			</ItemWrapper>
		);
	} else if (isButtonItemConfig(item)) {
		const { label, labelElem, ...buttonMenuItemProps } = omitSafely(item, 'type');
		return (
			<ItemWrapper>
				<ButtonMenuItem
					{...buttonMenuItemProps}
					onClick={(...args) => {
						buttonMenuItemProps.onClick?.(...args);
						onNavItemClick();
						onClick?.();
					}}
					hasDragIndicator={isItemDraggable}
					ref={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
					actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
				>
					{labelElem ?? label}
				</ButtonMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isExpandableItemConfig(item)) {
		// eslint-disable-next-line no-unused-vars
		const { children, elemBefore, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onExpansionToggle', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<ItemWrapper>
				<ExpandableMenuItem
					dropIndicator={dropIndicator}
					isExpanded={isExpandOrFlyoutOpen}
					onExpansionToggle={onExpandFlyoutToggle}
					ref={dropTargetRef}
				>
					<ExpandableMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						// During drags, custom elemBefore is not shown
						// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
						elemBefore={isDragging ? null : elemBefore}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						actionsOnHover={getActionsOnHover({ id, isItemDraggable, item, isInMoreMenu, group })}
					>
						{labelElem ?? label}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>{wrappedChildren}</ExpandableMenuItemContent>
				</ExpandableMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isFlyoutItemConfig(item)) {
		// eslint-disable-next-line no-unused-vars
		const { children, label, labelElem, ...triggerProps } = omitSafely(
			item,
			'type',
			'isDefaultOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
		);

		// Wrap children with our analytics listener to capture their click events
		const wrappedChildren = withSideNavigationAnalyticsListener(item.children, onSubMenuItemClick);

		return (
			<ItemWrapper>
				<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
					<FlyoutMenuItemTrigger
						{...triggerProps}
						onClick={(...args) => {
							triggerProps.onClick?.(...args);
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{labelElem ?? label}
					</FlyoutMenuItemTrigger>
					<FlyoutMenuItemContent onClose={() => onExpandFlyoutToggle(false)}>
						{wrappedChildren}
					</FlyoutMenuItemContent>
				</FlyoutMenuItem>
				{maybeDragPreview}
			</ItemWrapper>
		);
	} else if (isObjectListFlyoutItemConfig(item)) {
		const objectListFlyoutProps = omitSafely(
			item,
			'type',
			'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
			'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
		);

		const isOpenExternallyControlled = typeof item.isOpen === 'boolean';
		// We only want to control the flyout if it is already open, or when we are doing a drag operation.
		// In other cases, we should return undefined to signal that the OLF is not controlled.
		const isControlledFlyout = isOpenExternallyControlled
			? item.isOpen
			: isExpandOrFlyoutOpen || isDragging
				? isExpandOrFlyoutOpen
				: undefined;

		return (
			<ItemWrapper>
				<ElementEventListenerProvider handler={onSubMenuItemClick}>
					<ObjectListFlyout
						{...objectListFlyoutProps}
						itemMenuId={id}
						onClick={(...args) => {
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							(objectListFlyoutProps as ObjectListFlyoutProps).onClick?.(...args);
							fg('navx-global-side-nav-confluence')
								? onNavItemClick(false, isExpandOrFlyoutOpen ? 'collapsed' : 'expanded')
								: onNavItemClick();
							onClick?.();
						}}
						onSubMenuItemClick={(event, extendedSubItemAnalyticsAttributes) => {
							(objectListFlyoutProps as ObjectListFlyoutProps).onSubMenuItemClick?.(
								event,
								extendedSubItemAnalyticsAttributes,
							);
							onSubMenuItemClick(false, extendedSubItemAnalyticsAttributes);
						}}
						isFlyoutOpen={isControlledFlyout}
						onOpenChange={onExpandFlyoutToggle}
						hasDragIndicator={isItemDraggable}
						triggerRef={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					/>
					{maybeDragPreview}
				</ElementEventListenerProvider>
			</ItemWrapper>
		);
	} else {
		return null;
	}
}

type ActionsOnHoverProps = {
	group?: ItemDragAndDropGroup;
	id: string;
	isInMoreMenu?: boolean;
	isItemDraggable: boolean;
	item: ButtonItemConfig | ExpandableItemConfig | LinkItemConfig;
};

// This cannot be a React function-component, otherwise the Link/Button/Expand.actionsOnHover prop will can never be undefined.
// When there are no actionsOnHover and the item is not draggable, actionsOnHover MUST return undefined, to avoid the
// elemAfter from being hidden unintentionally.
function getActionsOnHover({ item }: ActionsOnHoverProps) {
	return item.actionsOnHover ?? null;
}
